import { Text } from '@soteria/design-system-ve';

import * as styles from './CommunityGroupStats.css';

export interface StatItem {
  /** The label text displayed above the value */
  label: string;

  /** The value to display (can be a string or number) */
  value: string | number;

  /** Size variant for the component */
  size?: 'small' | 'medium' | 'large';

  /**
   * Visual style variant
   * - default: Uses primary content color
   * - primary: Uses blue500 color
   * - success: Uses green500 color with matching left border
   * - error: Uses red500 color with matching left border
   * - warning: Uses orange500 color with matching left border
   * - info: Uses blue500 color with matching left border
   * - ai: Uses purple500 color with matching left border
   */
  variant?: 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info' | 'ai';

  /** Optional CSS class name for individual stat */
  className?: string;
}

interface CommunityGroupStatsProps {
  /**
   * Array of stat items to render
   */
  stats: StatItem[];

  /**
   * Optional background color for the grid container
   */
  backgroundColor?: string;

  /**
   * Optional additional className to apply to the grid container
   */
  className?: string;

  /**
   * Optional data-testid for testing
   */
  testId?: string;

  /**
   * Optional inline style object
   */
  style?: React.CSSProperties;
}

/**
 * CommunityGroupStats component
 *
 * A reusable grid layout for displaying statistics in a consistent format.
 * Automatically renders stats based on the provided stats array.
 */
export function CommunityGroupStats({ stats, backgroundColor, className, style, testId = 'community-group-stats' }: CommunityGroupStatsProps) {
  return (
    <div className={`${styles.statsGrid} ${className || ''}`} data-testid={testId} style={{ ...style, backgroundColor }}>
      {stats.map(stat => {
        const { label, value, size = 'medium', variant = 'default', className: statClassName } = stat;

        // Create stats class
        const statsClass = styles.stats({ size, variant });

        // Combine with optional className and centered stats class
        const combinedClassName = statClassName ? `${statsClass} ${styles.centeredStats} ${statClassName}` : `${statsClass} ${styles.centeredStats}`;

        // Create value class
        const valueClass = styles.valueStyle({ size, variant });

        return (
          <div key={label} className={combinedClassName}>
            <div className={styles.labelStyle}>
              <Text as="span" color="secondary" size="sm" weight="medium">
                {label}
              </Text>
            </div>
            <Text as="div" className={valueClass}>
              {value}
            </Text>
          </div>
        );
      })}
    </div>
  );
}
